import { create as createNiceModal, useModal } from '@ebay/nice-modal-react'
import InfoIcon from '@mui/icons-material/Info'
import WarningIcon from '@mui/icons-material/Warning'
import {
  cartSlice,
  getCartMode,
  isSameVehicle,
  IVehiclePriceChangedParts,
  productsMatch,
  SellerCartVehicle,
} from 'cart'
import { I18n, Translate2 as Translate } from 'common'
import { observer } from 'mobx-react-lite'
import { setOrderResponses } from 'orderConfirmation'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CartMode } from 'src/store/models/ShoppingCartModels'
import styled from 'styled-components'
import { Vehicle } from '../../../../src/store/models/Vehicles'
import { StoreInstances } from '../../../../src/store/StoreInstancesContainer'
import { defaultTheme } from '../../../../src/theme'
import { SimpleModal } from '../../../../src/ui-components'
import { VehicleWithPriceChangedProducts } from './VehicleWithPriceChangedProducts'
import { useSelector } from 'react-redux'
import { RootState, useAppDispatch } from 'store'
import { IRemovePartFromCartProps } from '../store/ISellerCart'
import { getVehicleWithPriceChangedParts } from '../utils/CartValidations'
import { placeOrder } from '../utils/orderUtils'

interface CartPriceChangedProductsModalProps {
  title: string
  primaryButtonText: string
  secondaryButtonText: string
  vehiclePriceChangeErrors: SellerCartVehicle[]
  mainSellerVehicleData?: SellerCartVehicle[]
}

export const CartPriceChangedProductsModal =
  createNiceModal<CartPriceChangedProductsModalProps>(
    observer(
      ({
        title,
        primaryButtonText,
        secondaryButtonText,
        vehiclePriceChangeErrors,
        mainSellerVehicleData,
      }): ReactElement => {
        const dispatch = useAppDispatch()
        const navigate = useNavigate()
        const modal = useModal()
        const processingOrder = useSelector(
          (state: RootState) => state.ui.processingOrder
        )

        const [vehicleWithPriceChangedParts, setVehicleWithPriceChangedParts] =
          useState<IVehiclePriceChangedParts[]>([])

        const updatedCartData = useSelector((state: RootState) => {
          if (mainSellerVehicleData) {
            return state.cart.cartData.vehicles.filter((v) =>
              mainSellerVehicleData.find((mv) =>
                isSameVehicle(mv.vehicle, v.vehicle)
              )
            )
          }
          return state.cart.cartData.vehicles
        })

        const [mainVehicleCartData, setMainVehicleCartData] = useState<
          SellerCartVehicle[]
        >(mainSellerVehicleData || [])

        useEffect(() => {
          setMainVehicleCartData(updatedCartData)
        }, [updatedCartData, mainSellerVehicleData])

        useEffect(() => {
          setVehicleWithPriceChangedParts(
            getVehicleWithPriceChangedParts(vehiclePriceChangeErrors)
          )
        }, [vehiclePriceChangeErrors])

        const removeCartFromOrder = (vehicle: Vehicle): void => {
          // Remove the vehicle from the state array by filtering it out
          setVehicleWithPriceChangedParts((prevState) =>
            prevState.filter((item) => !isSameVehicle(item.vehicle, vehicle))
          )

          setMainVehicleCartData((prevState) =>
            prevState.filter((item) => !isSameVehicle(item.vehicle, vehicle))
          )
        }

        const removeItemFromOrder = ({
          vehicle,
          parts,
        }: IVehiclePriceChangedParts): void => {
          setVehicleWithPriceChangedParts((prevState) =>
            prevState
              .map((item) => {
                // If it's not the same vehicle, leave it unchanged
                if (!isSameVehicle(item.vehicle, vehicle)) return item

                // Remove the part from price changed products data
                const updatedParts = item.parts.filter(
                  (existingPart) =>
                    !parts.some((partToRemove) =>
                      productsMatch(existingPart.product, partToRemove.product)
                    )
                )

                return { ...item, parts: updatedParts }
              })
              // Optionally remove vehicles that have no remaining parts
              .filter((item) => item.parts.length > 0)
          )
        }

        const handleSecondaryAction = useCallback(() => {
          modal.remove()
        }, [modal])

        useEffect(() => {
          if (vehicleWithPriceChangedParts.length === 0) {
            handleSecondaryAction()
          }
        }, [handleSecondaryAction, vehicleWithPriceChangedParts.length])

        const removePlacedPartsFromCartDispatch = (
          updatedCartToPlaceOrder: SellerCartVehicle[]
        ): void => {
          updatedCartToPlaceOrder.forEach((vehicle) => {
            vehicle.sellers.forEach((seller) => {
              seller.locations.forEach((location) => {
                location.parts.forEach((part) => {
                  const payload: IRemovePartFromCartProps = {
                    product: part,
                    quantity: part.quantityRequested,
                    locationId: location.locationId.toString(),
                    seller,
                    vehicle: vehicle.vehicle,
                    showToastMessage: false,
                  }
                  dispatch(cartSlice.actions.removePartFromCart(payload))
                })
              })
            })
          })
        }

        const getRemovedPriceOverrideParts = (
          vehiclePriceChangeErrors: SellerCartVehicle[],
          visiblePriceErrors: IVehiclePriceChangedParts[],
          getVehicleWithPriceChangedParts: (
            data: SellerCartVehicle[]
          ) => IVehiclePriceChangedParts[]
        ): IVehiclePriceChangedParts[] => {
          const originalPriceErrors = getVehicleWithPriceChangedParts(
            vehiclePriceChangeErrors
          )

          return originalPriceErrors
            .map((originalVehicle) => {
              const visibleVehicle = visiblePriceErrors.find((v) =>
                isSameVehicle(v.vehicle, originalVehicle.vehicle)
              )

              // Entire vehicle removed
              if (!visibleVehicle) {
                return originalVehicle
              }

              const removedParts = originalVehicle.parts.filter(
                (originalPart) =>
                  !visibleVehicle.parts.some(
                    (visiblePart) =>
                      productsMatch(
                        originalPart.product,
                        visiblePart.product
                      ) &&
                      originalPart.seller.id === visiblePart.seller.id &&
                      originalPart.location.locationId.toString() ===
                        visiblePart.location.locationId.toString()
                  )
              )

              if (removedParts.length === 0) return null

              return {
                vehicle: originalVehicle.vehicle,
                parts: removedParts,
              }
            })
            .filter(Boolean) as IVehiclePriceChangedParts[]
        }

        const removePartsFromMainCart = (
          mainData: SellerCartVehicle[],
          removedVehicles: IVehiclePriceChangedParts[]
        ): SellerCartVehicle[] => {
          return mainData
            .map((vehicle) => {
              const removedForVehicle = removedVehicles.find((v) =>
                isSameVehicle(v.vehicle, vehicle.vehicle)
              )

              if (!removedForVehicle) return vehicle

              return {
                ...vehicle,
                sellers: vehicle.sellers
                  .map((seller) => ({
                    ...seller,
                    locations: seller.locations
                      .map((location) => ({
                        ...location,
                        parts: location.parts.filter(
                          (part) =>
                            !removedForVehicle.parts.some(
                              (removed) =>
                                productsMatch(part, removed.product) &&
                                seller.id === removed.seller.id &&
                                location.locationId.toString() ===
                                  removed.location.locationId.toString()
                            )
                        ),
                      }))
                      .filter((location) => location.parts.length > 0),
                  }))
                  .filter((seller) => seller.locations.length > 0),
              }
            })
            .filter((vehicle) => vehicle.sellers.length > 0)
        }

        const handlePrimaryAction = async (): Promise<void> => {
          try {
            const removedPriceChangeParts = getRemovedPriceOverrideParts(
              vehiclePriceChangeErrors,
              vehicleWithPriceChangedParts,
              getVehicleWithPriceChangedParts
            )

            const updatedCartToPlaceOrder = removePartsFromMainCart(
              mainVehicleCartData,
              removedPriceChangeParts
            )

            const response = await placeOrder(updatedCartToPlaceOrder)
            if (!response) return
            if (getCartMode() === CartMode.CART_ONLY) return
            setOrderResponses(response)
            removePlacedPartsFromCartDispatch(updatedCartToPlaceOrder)
            navigate('/orderConfirmation')
            modal.remove()
          } catch (_e) {
            StoreInstances.uiStore.displayErrorNotification(
              Translate('unableToPlaceOrder')
            )
          }
        }

        return (
          <SimpleModal
            show={modal.visible}
            title={title}
            primaryButtonVariant="primary"
            size="lg"
            onPrimaryAction={() => {
              handlePrimaryAction()
            }}
            onSecondaryAction={() => handleSecondaryAction()}
            primaryActionText={primaryButtonText}
            secondaryActionText={secondaryButtonText}
            borderBottomHeader
            loading={processingOrder}
            icon={
              <WarningIcon
                style={{
                  color: defaultTheme.colors.primary,
                  marginLeft: '10px',
                  marginRight: '10px',
                }}
              />
            }
          >
            <ModalContent>
              <InfoMessage>
                <StyleInfoIcon
                  style={{ color: defaultTheme.colors.greyDark }}
                />
                <I18n name="pleaseReviewTheFollowingMessageBeforeConfirmingYourOrder" />
              </InfoMessage>
              {vehicleWithPriceChangedParts.length > 0 &&
                vehicleWithPriceChangedParts.map((data, index) => (
                  <VehicleWithPriceChangedProducts
                    vehicleWithParts={data}
                    key={`${data}_${index}`}
                    removeCartFromOrder={removeCartFromOrder}
                    removeItemFromCart={removeItemFromOrder}
                  />
                ))}
            </ModalContent>
          </SimpleModal>
        )
      }
    )
  )

const ModalContent = styled.div`
  padding-left: 10px;
  padding-right: 10px;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
`

const InfoMessage = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${defaultTheme.colors.greyVeryLight};
  padding: 7px 16px;
  font-size: 14px;
  margin-top: 24px;
  min-width: 0;
`

const StyleInfoIcon = styled(InfoIcon)`
  font-size: 18px;
  margin-right: 10px;
`
